import * as React from 'react'
import { Focusable } from './Focusable'
import { classNames } from './helpers'
import type { LinkComponent } from '../types'

export interface PageNoticeAction {
  /** What the control reads as. Translated by the caller — the styleguide ships no copy. */
  label: string;
  /** Where it leads. An in-app route when a `LinkComponent` is supplied. */
  href: string;
}

export interface PageNoticeProps {
  /**
   * A single emoji setting the tone, rendered large above the title. Decorative and
   * hidden from assistive tech — the title carries the meaning. Optional, because a
   * notice that appears inside a busy page may not want one.
   */
  glyph?: string;
  /** The headline. One short sentence; it is an `<h1>` — see the note below. */
  title: string;
  /** What happened, and what it means for the viewer. A sentence or two. */
  message: string;
  /** The single way forward. Omit for a notice the viewer can do nothing about. */
  action?: PageNoticeAction;
  /**
   * In-app link component (e.g. React Router's `Link`). Without it the action falls
   * back to a plain `<a>`, which is a full page load — correct, but slower.
   */
  LinkComponent?: LinkComponent;
  className?: string;
}

/**
 * A page that has nothing to show, and says so: an article that does not exist, a
 * topic that has been removed, a title whose details would not load.
 *
 * It is the in-page relative of the shell's startup error screen — same shape
 * (glyph, headline, a sentence, one way out), same unhurried tone — but it is
 * *content*, not chrome: the header, nav and footer stay, so the viewer is still
 * somewhere rather than nowhere, and the way out is an ordinary in-app link rather
 * than a reload. The shell's version cannot be reused for this; it is fixed to the
 * viewport and belongs to a shell that has not started, and pilets cannot import
 * from the shell at all.
 *
 * **The heading is an `<h1>` on purpose.** This replaces a detail page's whole
 * content, including the title that would have been the page's h1, so a document
 * that skipped straight to h2 would leave a screen-reader user without a page
 * heading. Never render it beside other page content for that reason — it is a
 * whole-page state, not a panel.
 *
 * `role='status'` rather than `alert`: nothing is on fire and nothing is being
 * interrupted. The page simply resolved to this, and it is announced politely.
 */
export function PageNotice ({ glyph, title, message, action, LinkComponent, className }: PageNoticeProps) {
  return (
    <div className={classNames('mlsg-page-notice', className)} role='status'>
      <div className='mlsg-page-notice__content'>
        {glyph ? <p className='mlsg-page-notice__glyph' aria-hidden='true'>{glyph}</p> : null}
        <h1 className='mlsg-page-notice__title'>{title}</h1>
        <p className='mlsg-page-notice__message'>{message}</p>
        {action
          ? (
            // Its own spatial-navigation stop, so a remote can reach the only
            // control on the page — the same treatment the breadcrumb's links get.
            <Focusable className='mlsg-focusable--inline'>
              {LinkComponent
                ? <LinkComponent to={action.href} className='mlsg-page-notice__action'>{action.label}</LinkComponent>
                : <a href={action.href} className='mlsg-page-notice__action'>{action.label}</a>}
            </Focusable>
            )
          : null}
      </div>
    </div>
  )
}
